import { motion, AnimatePresence } from 'framer-motion';
import { PlayerPublic } from '../shared/types';
import { PlayingCard } from './Card';
import { ChipStack } from './Chip';
import { ChipIcon } from './ChipIcon';
import { ActionTimer } from './Timer';

interface PlayerSeatProps {
  player: PlayerPublic | null; // null = свободное место
  isViewer: boolean; // это место текущего пользователя
  isActing: boolean;
  actionDeadline: number | null;
  winningsAmount?: number; // если игрок выиграл эту раздачу
  onSitHere?: () => void;
  seatPositionClass: string; // tailwind absolute positioning
}

export function PlayerSeat({
  player,
  isViewer,
  isActing,
  actionDeadline,
  winningsAmount,
  onSitHere,
  seatPositionClass,
}: PlayerSeatProps) {
  if (!player) {
    return (
      <div className={`absolute ${seatPositionClass}`}>
        <button
          onClick={onSitHere}
          className="w-16 h-16 rounded-full border-2 border-dashed border-white/15 text-white/30 text-xs hover:border-gold-500/50 hover:text-gold-400 transition-colors flex items-center justify-center"
        >
          Сесть
        </button>
      </div>
    );
  }

  return (
    <div className={`absolute ${seatPositionClass} flex flex-col items-center gap-1`}>
      <AnimatePresence>
        {winningsAmount ? (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.5 }}
            animate={{ opacity: 1, y: -10, scale: [0.5, 1.3, 1] }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5, times: [0, 0.6, 1] }}
            className="absolute -top-7 text-emerald-400 font-bold text-sm drop-shadow-[0_0_6px_rgba(46,235,130,0.6)] z-10"
          >
            +{winningsAmount.toLocaleString('ru-RU')}
          </motion.div>
        ) : null}
      </AnimatePresence>

      <div
        className={`relative w-16 h-16 rounded-full overflow-hidden border-2 ${
          isActing ? 'border-emerald-400 shadow-goldGlow animate-pulseGlow' : 'border-white/15'
        } ${winningsAmount ? 'border-gold-400 shadow-goldGlow' : ''} ${player.isFolded ? 'opacity-40 grayscale' : ''} ${
          !player.isConnected ? 'opacity-50' : ''
        }`}
      >
        {player.avatarUrl ? (
          <img src={player.avatarUrl} className="w-full h-full object-cover" alt={player.username} />
        ) : (
          <div className="w-full h-full bg-ink-700 flex items-center justify-center text-lg font-display text-gold-400">
            {player.username.slice(0, 1).toUpperCase()}
          </div>
        )}

        {player.isDealer && (
          <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-gold-400 border border-ink-900 flex items-center justify-center text-[10px] font-bold text-ink-900">
            D
          </div>
        )}
      </div>

      <div className="text-center">
        <div className="text-[11px] font-semibold text-white/90 max-w-[72px] truncate">{player.username}</div>
        <div className="text-[10px] text-gold-400 tabular-nums flex items-center justify-center gap-0.5">
          <ChipIcon size={9} />
          {player.stack.toLocaleString('ru-RU')}
        </div>
      </div>

      {isActing && <ActionTimer deadline={actionDeadline} compact />}

      {player.betInStreet > 0 && (
        <div className="absolute -bottom-6">
          <ChipStack amount={player.betInStreet} />
        </div>
      )}

      {(player.cards && player.cards.length > 0) || (player.hasCards && !player.isFolded) ? (
        <div className="absolute -top-8 flex gap-0.5">
          {isViewer && player.cards
            ? player.cards.map((c, i) => <PlayingCard key={i} card={c} size="sm" delay={i * 0.05} />)
            : [0, 1].map((i) => <PlayingCard key={i} card={null} faceDown size="sm" delay={i * 0.05} />)}
        </div>
      ) : null}

      {player.isSittingOut && (
        <div className="absolute inset-0 flex items-center justify-center bg-ink-950/60 rounded-full text-[9px] text-white/70">
          АФК
        </div>
      )}
    </div>
  );
}
